import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy, Timestamp, where, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuth } from 'firebase/auth';
import { UserRole, AgentType } from '../types/auth';
import { permissionService } from './permissionService';

export interface Client {
  id?: string;
  name: string;
  phoneNumber: string;
  collectionCentre: string;
  date: string;
  agentType?: AgentType; // Add agent type to client
}

class ClientService {
  private readonly COLLECTION_NAME = 'clients';

  private checkAuth() {
    const auth = getAuth();
    console.log('Checking authentication state...');
    console.log('Current user:', auth.currentUser);
    
    if (!auth.currentUser) {
      console.error('No authenticated user found');
      throw new Error('Please log in to continue');
    }
    
    console.log('User is authenticated:', {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email
    });
  }

  // Get user role and agent type from auth context
  private getUserInfo(): { role: UserRole; agentType?: AgentType } {
    // This would typically come from the auth context
    // For now, we'll use a simple email-based approach
    const auth = getAuth();
    const email = auth.currentUser?.email || '';
    
    if (email.includes('admin') || email.includes('super')) {
      return { role: 'super_admin' };
    }
    
    // Extract agent type from email or other identifier
    // This is a simplified approach - in production, this should come from user profile
    return { role: 'admin' };
  }

  // Add a new client
  async addClient(client: Omit<Client, 'id'>, userRole: UserRole, userAgentType?: AgentType): Promise<Client> {
    try {
      // Check permissions
      if (!permissionService.canPerformAction(userRole, userAgentType, 'canAddClients')) {
        throw new Error('You do not have permission to add clients');
      }

      // Check if phone number already exists
      const phoneQuery = query(
        collection(db, this.COLLECTION_NAME),
        where('phoneNumber', '==', client.phoneNumber)
      );
      const phoneSnapshot = await getDocs(phoneQuery);
      
      if (!phoneSnapshot.empty) {
        throw new Error('A client with this phone number already exists');
      }

      // For admin users, automatically set the agent type based on their selection
      let clientData = {
        ...client,
        date: Timestamp.now().toDate().toISOString()
      };

      if (userRole === 'admin' && userAgentType) {
        clientData.agentType = userAgentType;
        clientData.collectionCentre = permissionService.getCollectionCenter(userAgentType);
      }

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), clientData);
      
      console.log('Client added successfully with ID:', docRef.id);
      return {
        id: docRef.id,
        ...clientData
      };
    } catch (error) {
      console.error('Error adding client:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  }

  // Get all clients
  async getClients(userRole: UserRole, userAgentType?: AgentType): Promise<Client[]> {
    try {
      this.checkAuth();
      console.log('Fetching clients...');
      
      let q;
      
      if (userRole === 'super_admin') {
        // Super admin can see all clients
        q = query(
          collection(db, this.COLLECTION_NAME),
          orderBy('date', 'desc')
        );
      } else if (userRole === 'admin' && userAgentType) {
        // Admin users can only see clients from their assigned collection center
        const collectionCentre = permissionService.getCollectionCenter(userAgentType);
        console.log('Filtering clients by collection centre:', collectionCentre);
        
        try {
          // Try the composite query first
          q = query(
            collection(db, this.COLLECTION_NAME),
            where('collectionCentre', '==', collectionCentre),
            orderBy('date', 'desc')
          );
        } catch (indexError) {
          console.warn('Composite index not ready, falling back to simple query:', indexError);
          // Fallback: get all clients and filter in memory
          q = query(
            collection(db, this.COLLECTION_NAME),
            orderBy('date', 'desc')
          );
        }
      } else {
        throw new Error('Invalid user role or agent type');
      }
      
      const querySnapshot = await getDocs(q);
      console.log('Query snapshot received, size:', querySnapshot.size);
      
      let clients = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const client = {
          id: doc.id,
          ...data
        } as Client;
        console.log('Client document:', doc.id, data, 'Processed client:', client);
        return client;
      });
      
      // If we're an admin and used the fallback query, filter by collection centre in memory
      if (userRole === 'admin' && userAgentType && !clients[0]?.collectionCentre) {
        const collectionCentre = permissionService.getCollectionCenter(userAgentType);
        clients = clients.filter(client => client.collectionCentre === collectionCentre);
        console.log('Filtered clients in memory, final count:', clients.length);
      }
      
      console.log('Clients fetched successfully:', clients);
      console.log('Total clients found:', clients.length);
      return clients;
    } catch (error) {
      console.error('Error getting clients:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  }

  // Update a client
  async updateClient(id: string, client: Partial<Client>, userRole: UserRole, userAgentType?: AgentType): Promise<void> {
    try {
      this.checkAuth();
      
      // Check permissions
      if (!permissionService.canPerformAction(userRole, userAgentType, 'canModifyClients')) {
        throw new Error('You do not have permission to modify clients');
      }
      
      // Get the existing client to check agent type
      const existingClientDoc = await getDoc(doc(db, this.COLLECTION_NAME, id));
      if (!existingClientDoc.exists()) {
        throw new Error('Client not found');
      }
      
      const existingClient = existingClientDoc.data() as Client;
      
      // Check if user can access this specific client
      if (existingClient.agentType && !permissionService.canAccessClient(userRole, userAgentType, existingClient.agentType)) {
        throw new Error('You do not have permission to modify this client');
      }
      
      console.log('Updating client:', id, client);
      // If phone number is being updated, check for uniqueness
      if (client.phoneNumber) {
        const phoneQuery = query(
          collection(db, this.COLLECTION_NAME),
          where('phoneNumber', '==', client.phoneNumber)
        );
        const phoneSnapshot = await getDocs(phoneQuery);
        
        // Check if the phone number exists in another client
        const phoneExists = phoneSnapshot.docs.some(doc => doc.id !== id);
        if (phoneExists) {
          throw new Error('A client with this phone number already exists');
        }
      }

      const clientRef = doc(db, this.COLLECTION_NAME, id);
      await updateDoc(clientRef, client);
      console.log('Client updated successfully');
    } catch (error) {
      console.error('Error updating client:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  }

  // Delete a client
  async deleteClient(id: string, userRole: UserRole, userAgentType?: AgentType): Promise<void> {
    try {
      this.checkAuth();
      
      // Check permissions
      if (!permissionService.canPerformAction(userRole, userAgentType, 'canModifyClients')) {
        throw new Error('You do not have permission to delete clients');
      }
      
      // Get the existing client to check agent type
      const existingClientDoc = await getDoc(doc(db, this.COLLECTION_NAME, id));
      if (!existingClientDoc.exists()) {
        throw new Error('Client not found');
      }
      
      const existingClient = existingClientDoc.data() as Client;
      
      // Check if user can access this specific client
      if (existingClient.agentType && !permissionService.canAccessClient(userRole, userAgentType, existingClient.agentType)) {
        throw new Error('You do not have permission to delete this client');
      }
      
      console.log('Deleting client:', id);
      const clientRef = doc(db, this.COLLECTION_NAME, id);
      await deleteDoc(clientRef);
      console.log('Client deleted successfully');
    } catch (error) {
      console.error('Error deleting client:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  }

  async getClientById(id: string): Promise<Client | null> {
    try {
      const clientRef = doc(db, this.COLLECTION_NAME, id);
      const clientDoc = await getDoc(clientRef);
      
      if (clientDoc.exists()) {
        return {
          id: clientDoc.id,
          ...clientDoc.data()
        } as Client;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting client by ID:', error);
      throw error;
    }
  }
}

export const clientService = new ClientService(); 